import { useEffect, useRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import { Button, Form } from "react-bootstrap";
import PropTypes from "prop-types";
import "./ProfileUploaderContent.scss";
import { BiUpload } from "react-icons/bi";
import Spinner from "./app/common-components/Spinner";
import { user } from "./assets/images";

export default function ProfileUploaderContent({
  preview,
  alert,
  onFileChange,
  onSave,
  onRemove,
  isLoading,
  hasFileChanged,
  usersProfilePic,
  selectedFile,
  isImageRemoved,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [livePreview, setLivePreview] = useState(null);

  const inputRef = useRef();
  const editorRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      onFileChange(e.dataTransfer.files[0]);
      if (inputRef.current) {inputRef.current.value = null;}
    }
  };

  const updateLivePreview = () => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      setLivePreview(canvas.toDataURL("image/png"));
    }
  };

  const handleSave = () => {
    if (isImageRemoved) {
      onSave(null);
      return;
    }
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const croppedDataUrl = canvas.toDataURL("image/png");

      onSave(croppedDataUrl); // ⬅ base64 will be converted to File in parent
    }
  };

  useEffect(() => {
    if (selectedFile) {
      setTimeout(updateLivePreview, 100); // small delay to allow image load
    }
  }, [selectedFile, zoom]);

  return (
    <>
      <h4>Profile</h4>
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="d-flex flex-column align-items-center gap-3">
            <div
              className={`profile-uploader-wrapper position-relative ${
                isDragging ? "dragging" : ""
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <AvatarEditor
                  ref={editorRef}
                  image={selectedFile}
                  width={150}
                  height={150}
                  border={30}
                  borderRadius={75}
                  color={[255, 255, 255, 0.6]}
                  scale={zoom}
                  rotate={0}
                  onMouseUp={updateLivePreview}
                  onTouchEnd={updateLivePreview}
                />
              ) : (
                <img
                  src={preview || user}
                  alt="Profile Preview"
                  className={isDragging ? "dragging" : ""}
                />
              )}
            </div>

            {selectedFile && preview && (
              <div
                className="live-preview text-center"
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "2px solid #ccc",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                <img
                  src={livePreview || preview}
                  alt="Live Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            )}
          </div>

          {selectedFile && (
            <div className="w-75 mx-auto mb-3">
              <Form.Label className="mb-0">Zoom</Form.Label>

                <Form.Range
                  min={1}
                  max={2.5}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => {
                    const zoomVal = parseFloat(e.target.value);
                    setZoom(zoomVal);
                    updateLivePreview(); // 👈 update live preview on zoom
                  }}
                  style={{ cursor: "pointer" }}
                />
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            ref={inputRef}
            onChange={(e) => {
              const selected = e.target.files[0];
              if (selected) {
                onFileChange(selected);
              }
              e.target.value = null;
            }}
            hidden
          />

          <span
            className={`d-flex justify-content-center ${
              alert.type === "danger" ? "text-danger" : ""
            }`}
          >
            {alert.message}
          </span>

          <div className="mt-3 d-flex justify-content-between">
            <Button
              onClick={() => {
                onRemove();
                if (inputRef.current) {inputRef.current.value = null;}
              }}
              disabled={isImageRemoved || (!usersProfilePic && !selectedFile)}
            >
              Remove
            </Button>

            <Button onClick={() => inputRef.current.click()}>
              <BiUpload /> Upload
            </Button>

            <Button
              onClick={handleSave}
              disabled={
                (!hasFileChanged && !isImageRemoved) ||
                (!usersProfilePic && isImageRemoved && !selectedFile)
              }
            >
              Save
            </Button>
          </div>
        </>
      )}
    </>
  );
}

ProfileUploaderContent.propTypes = {
  preview: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)])
    .isRequired,
  alert: PropTypes.shape({
    type: PropTypes.oneOf(["success", "danger", "warning", "info"]),
    message: PropTypes.string,
  }).isRequired,
  onFileChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  hasFileChanged: PropTypes.bool,
  usersProfilePic: PropTypes.string,
  selectedFile: PropTypes.instanceOf(File),
  isImageRemoved: PropTypes.bool,
};
